import React from 'react'
import type { ReactElement, FocusEvent, KeyboardEvent, SetStateAction } from 'react'

import type { MultiValue } from 'react-select'
import ReactSelect from 'react-select'
import CreatableSelect from 'react-select/creatable'

import { ErrorMessage } from '../ErrorMessage'
import { Label } from '../Label'

type Value = string | number | boolean | undefined
export type ValueLabelPair = { value: number | string; label: string | JSX.Element }

type OnChangeType =
  | ((args: MultiValue<ValueLabelPair> | null) => void)
  | ((args: SetStateAction<ValueLabelPair[]>) => void)

export const createOption = (label: string) => ({
  label,
  value: label,
})

type MultiSelectProp<Value> = {
  id: string
  options: ValueLabelPair[]
  value: Value[]
  name?: string
  label?: string | ReactElement
  defaultValue?: ValueLabelPair[]
  inputValue?: string
  capitalizeLabel?: boolean
  placeholder?: string
  error?: string
  touched?: boolean
  marginBottom?: number
  isLoading?: boolean
  isClearable?: boolean
  isDisabled?: boolean
  isCreatable?: boolean
  onChange: OnChangeType
  onBlur?: (e: FocusEvent<unknown, Element>) => void
  onInputChange?: (e: React.SetStateAction<string>) => void
  handleKeyDown?: (e: KeyboardEvent<Element>) => void
}

export const MultiSelect = <T extends Value>({
  id,
  options,
  value,
  name,
  label,
  defaultValue,
  inputValue,
  capitalizeLabel,
  placeholder,
  error,
  touched,
  marginBottom,
  isLoading,
  isClearable = false,
  isDisabled,
  isCreatable = false,
  onChange,
  onBlur,
  onInputChange,
  handleKeyDown,
}: MultiSelectProp<T>): ReactElement => {
  const currentValue =
    value === undefined
      ? defaultValue
      : (value.map(val => options.find(option => option.value === val)) as ValueLabelPair[])

  const customStyle = {
    control: (baseStyles: any, state: any) => ({
      ...baseStyles,
      padding: '0.25rem 0',
      borderRadius: '0.5rem',
      ...(state.isFocused && {
        borderColor: '#ff6000',
        boxShadow: '0 0 0 0.075rem #ff6000',
      }),
      ':hover': {
        ...baseStyles[':hover'],
        borderColor: '#ff6000',
      },
      ...(error &&
        touched && {
          borderColor: '#e74c3c',
          boxShadow: '0 0 0 0.075rem #e74c3c',
        }),
    }),
  }

  return (
    <div style={{ marginBottom: marginBottom || 0 }}>
      {label && (
        <Label capitalizeLabel={capitalizeLabel} htmlFor={id}>
          {label}
        </Label>
      )}
      {isCreatable ? (
        <CreatableSelect
          components={{
            DropdownIndicator: null,
          }}
          inputValue={inputValue}
          isClearable={isClearable}
          isMulti
          menuIsOpen={false}
          styles={customStyle}
          onChange={onChange}
          onInputChange={onInputChange}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          placeholder={placeholder}
          value={options}
        />
      ) : (
        <ReactSelect
          isMulti
          id={id}
          name={name}
          options={options}
          styles={customStyle}
          value={currentValue}
          placeholder={placeholder}
          isLoading={isLoading}
          isClearable={isClearable}
          isDisabled={isDisabled}
          onChange={onChange}
          // Crucial for setting the "touched" fields
          onBlur={onBlur}
        />
      )}
      {error && touched && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  )
}
